// /account → unified "Usage" card.
//
// Single billing-context surface that merges three things that used to
// live in three separate cards (plan summary, monthly credits, daily
// caps): plan name + price, monthly credits remaining (one bar), and a
// collapsible breakdown of today's per-mode caps for free users. The
// goal is "share-easily" — a user can screenshot this one card and it
// fully explains where they stand without exposing PII beyond the
// plan name.
//
// Vocabulary stays billing-first: "credits" and "remaining". Numbers
// come from the same hooks that gate AI calls (useCreditLimit +
// useDailyUsage) so this card always matches what actually limits the
// user.
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Gauge,
  ArrowRight,
  Infinity as InfinityIcon,
  ChevronDown,
  Sparkles,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { useCreditLimit } from "@/hooks/useCreditLimit";
import { useDailyUsage, type UsageKind } from "@/hooks/useDailyUsage";
import { useEntitlements } from "@/hooks/useEntitlements";
import { useTimezonePref, formatInTz, getTzAbbr } from "@/hooks/useTimezonePref";
import { computeResetAt } from "@/lib/creditReset";

// Mirrors FREE_DAILY_LIMITS in HubUsageWidget / quota.ts. Only consulted
// for the free tier — paid tiers don't render the daily strip at all.
const FREE_DAILY_LIMITS: Record<UsageKind, number> = {
  chat: 20,
  image: 3,
  research: 3,
  guide: 2,
};

const DAILY_LABELS: Record<UsageKind, string> = {
  chat: "Chat",
  image: "Image",
  research: "Research",
  guide: "Guide",
};

const CreditsUsageCard = () => {
  const { data, loading, pct } = useCreditLimit();
  const { plan, planId } = useEntitlements();
  const { usage } = useDailyUsage();
  const [showDaily, setShowDaily] = useState(false);
  const { tz } = useTimezonePref();

  const used = data?.used ?? 0;
  const limit = data?.limit ?? null;
  const isUnlimited = limit === null;
  const remaining = isUnlimited ? null : Math.max(0, (limit ?? 0) - used);
  const pctNum = Math.round(pct * 100);
  const isFree = planId === "script_kiddie";

  // Next monthly refill, formatted in the user's preferred timezone.
  // `formatInTz` already appends the short zone label (e.g. "PDT");
  // `getTzAbbr` is used standalone where we just want the chip.
  const resetAt = computeResetAt(new Date());
  const resetFull = formatInTz(resetAt, tz);
  const resetTzAbbr = getTzAbbr(tz, resetAt);

  return (
    <section
      aria-label="Plan and credits usage"
      className="rounded-xl border border-border bg-card/60 p-5 sm:p-6"
    >
      <div className="flex items-start gap-4">
        <div className="rounded-lg bg-primary/15 p-2.5 shrink-0">
          <Gauge className="w-5 h-5 text-primary" />
        </div>
        <div className="flex-1 min-w-0">
          {/* Plan row — folded into the same card so users don't have
              to cross-reference a separate "plan" card to make sense
              of their credit balance. */}
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <div>
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                Your plan &amp; usage
              </p>
              <h2 className="font-mono font-bold text-base sm:text-lg text-foreground mt-0.5">
                {plan.name}
                <span className="text-muted-foreground font-normal text-sm ml-2">
                  · {plan.priceLabel}
                  {plan.priceMonthly > 0 && "/mo"}
                </span>
              </h2>
            </div>
            {isFree && (
              <Link
                to="/pricing"
                className="inline-flex items-center gap-1 text-[11px] font-mono font-semibold text-primary hover:text-primary/80 transition-colors"
              >
                <Sparkles className="w-3 h-3" />
                Upgrade
              </Link>
            )}
          </div>

          {/* Monthly credits — the headline number. Always one bar, one
              line of context. This is what matters when the user takes
              a screenshot. */}
          {loading && !data ? (
            <p className="text-xs text-muted-foreground mt-4 font-mono">
              Loading credit balance…
            </p>
          ) : isUnlimited ? (
            <div className="mt-4">
              <p className="font-mono font-extrabold text-2xl text-foreground inline-flex items-center gap-2">
                <InfinityIcon className="w-6 h-6 text-primary" />
                Unlimited credits this month
              </p>
              <p className="text-xs text-muted-foreground mt-1.5 font-mono">
                <span className="text-foreground">{used.toLocaleString()}</span>{" "}
                used so far.
              </p>
            </div>
          ) : (
            <div className="mt-4 space-y-2">
              <div className="flex items-baseline justify-between gap-2 font-mono">
                <p className="text-foreground">
                  <span className="font-extrabold text-2xl">
                    {(remaining ?? 0).toLocaleString()}
                  </span>{" "}
                  <span className="text-muted-foreground text-sm">
                    of {(limit ?? 0).toLocaleString()} credits left
                  </span>
                </p>
                <p className="text-xs text-muted-foreground tabular-nums">
                  {pctNum}% used
                </p>
              </div>
              <Progress value={pctNum} className="h-2" />
              <p
                className="text-[11px] font-mono text-muted-foreground"
                title={`Refill anchored to ${resetTzAbbr} (your preferred timezone)`}
              >
                Refills{" "}
                <span className="text-foreground">{resetFull}</span>{" "}
                <span className="opacity-70 uppercase tracking-wider text-[10px]">
                  ({resetTzAbbr})
                </span>
              </p>
            </div>
          )}

          {/* Daily caps — collapsed by default to keep the card scannable.
              Only meaningful on the free tier; paid tiers see the cap line
              swap out for an "unlimited daily" reassurance. */}
          {isFree && (
            <div className="mt-4 border-t border-border pt-3">
              <button
                type="button"
                onClick={() => setShowDaily((v) => !v)}
                aria-expanded={showDaily}
                className="w-full flex items-center justify-between gap-2 text-[11px] font-mono text-muted-foreground hover:text-foreground transition-colors"
              >
                <span>Today's per-mode caps (free tier)</span>
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform ${
                    showDaily ? "rotate-180" : ""
                  }`}
                />
              </button>
              {showDaily && (
                <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5">
                  {(Object.keys(FREE_DAILY_LIMITS) as UsageKind[]).map((k) => {
                    const cap = FREE_DAILY_LIMITS[k];
                    const u = usage[k] ?? 0;
                    return (
                      <li
                        key={k}
                        className="flex items-center justify-between text-[11px] font-mono"
                      >
                        <span className="text-muted-foreground">
                          {DAILY_LABELS[k]}
                        </span>
                        <span className="tabular-nums text-foreground">
                          {u}
                          <span className="text-muted-foreground">/{cap}</span>
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2">
            <Button asChild size="sm" variant="default">
              <Link to="/usage-limits">
                How credits work
                <ArrowRight className="w-3.5 h-3.5 ml-1" />
              </Link>
            </Button>
            <Button asChild size="sm" variant="outline">
              <Link to="/pricing#topup">Top up credits</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CreditsUsageCard;
